import { SVG_XMLNS } from '@lved/shared-core';
import { LaunchScreenCommand } from '@theia-lved-core/browser/panels/launch-screen/launch-screen-contribution';
import { StorageService } from '@theia/core/lib/browser';
import { Widget } from '@theia/core/lib/browser/widgets';
import { CommandService } from '@theia/core/lib/common/command';
import {
    BoxLayout,
    BoxPanel,
    Panel,
    PanelLayout,
    Title,
    Widget as LuminoWidget
} from '@theia/core/shared/@lumino/widgets';
import { inject, injectable } from '@theia/core/shared/inversify';
import type { AppSidePanelHandler } from './side-panel-handler';

const LVGL_LOGO_SVG = `<svg width="24" height="24" viewBox="0 0 36 37" fill="none" xmlns="${SVG_XMLNS}" aria-hidden="true">
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.15386 0.5C1.85976 0.5 1.97351e-05 2.35973 1.46929e-05 4.65384L2.44752e-06 10.1923C2.44752e-06 10.957 0.619915 11.5769 1.38462 11.5769H9.69231C11.2217 11.5769 12.4615 12.8168 12.4615 14.3462V22.6538C12.4615 23.4185 13.0815 24.0385 13.8462 24.0385H22.1538C23.6833 24.0385 24.9231 25.2783 24.9231 26.8077V35.1154C24.9231 35.8801 25.543 36.5 26.3077 36.5H31.8462C34.1403 36.5 36 34.6403 36 32.3462V4.65385C36 2.35974 34.1403 0.5 31.8462 0.5H4.15386ZM31.1539 7.42308C32.3009 7.42308 33.2308 6.49321 33.2308 5.34615C33.2308 4.1991 32.3009 3.26923 31.1539 3.26923C30.0068 3.26923 29.0769 4.1991 29.0769 5.34615C29.0769 6.49321 30.0068 7.42308 31.1539 7.42308Z" fill="currentColor"/>
<path d="M0.00016902 14.9461C0.00016902 14.1814 0.620083 13.5615 1.38478 13.5615H9.09248C9.85718 13.5615 10.4771 14.1814 10.4771 14.9461V22.6538C10.4771 23.4185 9.85718 24.0385 9.09248 24.0385H1.38478C0.620081 24.0385 0.00016902 23.4185 0.00016902 22.6538V14.9461Z" fill="currentColor"/>
<path d="M0 27.4077C0 26.643 0.619914 26.0231 1.38462 26.0231H9.09231C9.85701 26.0231 10.4769 26.643 10.4769 27.4077V35.1154C10.4769 35.8801 9.85701 36.5 9.09231 36.5H4.15384C1.85974 36.5 0 34.6403 0 32.3462V27.4077Z" fill="currentColor"/>
<path d="M12.4616 27.4077C12.4616 26.643 13.0815 26.0231 13.8462 26.0231H21.5539C22.3186 26.0231 22.9385 26.643 22.9385 27.4077V35.1154C22.9385 35.8801 22.3186 36.5 21.5539 36.5H13.8462C13.0815 36.5 12.4616 35.8801 12.4616 35.1154V27.4077Z" fill="currentColor"/>
</svg>`;

@injectable()
export class LeftPanelContribution {
    protected readonly leftSidebarTabOrder: readonly string[] = [
        'explorer-view-container',
        'search-view-container',
        'scm-view-container',
        'widget:lvgl-design-panel',
        'widget:lvgl-assets-panel',
        'chat-view-widget'
    ];

    @inject(StorageService)
    protected readonly storageService!: StorageService;

    @inject(CommandService)
    protected readonly commandService!: CommandService;

    createContainer(handler: AppSidePanelHandler): Panel {
        const contentBox = new BoxLayout({ direction: 'top-to-bottom', spacing: 0 });
        BoxPanel.setStretch(handler.toolBar, 0);
        contentBox.addWidget(handler.toolBar);
        BoxPanel.setStretch(handler.dockPanel, 1);
        contentBox.addWidget(handler.dockPanel);
        const contentPanel = new BoxPanel({ layout: contentBox });

        const sidebarContainerLayout = new PanelLayout();
        const sidebarContainer = new Panel({ layout: sidebarContainerLayout });
        sidebarContainer.addClass('theia-app-sidebar-container');
        sidebarContainerLayout.addWidget(handler.topMenu);
        sidebarContainerLayout.addWidget(this.createSidebarLogoWidget());
        sidebarContainerLayout.addWidget(handler.tabBar);
        sidebarContainerLayout.addWidget(handler.additionalViewsMenu);
        sidebarContainerLayout.addWidget(handler.bottomMenu);

        const containerLayout = new BoxLayout({ direction: 'left-to-right', spacing: 0 });
        BoxPanel.setStretch(sidebarContainer, 0);
        BoxPanel.setStretch(contentPanel, 1);
        containerLayout.addWidget(sidebarContainer);
        containerLayout.addWidget(contentPanel);

        const boxPanel = new BoxPanel({ layout: containerLayout });
        boxPanel.id = 'theia-left-content-panel';
        return boxPanel;
    }

    private createSidebarLogoWidget(): Widget {
        const widget = new Widget({ node: document.createElement('div') });
        widget.id = 'widget:lvgl-sidebar-logo';
        widget.title.closable = false;
        widget.node.className = 'lvgl-sidebar-logo';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lvgl-sidebar-logo-button';
        button.title = 'Project Launcher';
        button.setAttribute('aria-label', 'Open project launcher');
        button.innerHTML = LVGL_LOGO_SVG;
        button.addEventListener('click', () => {
            void this.commandService.executeCommand(LaunchScreenCommand.id);
        });

        widget.node.appendChild(button);
        return widget;
    }

    private tabOrder: readonly string[] | undefined;
    private tabOrderLoadStarted: boolean = false;
    private userHasReordered: boolean = false;
    private applyingOrder: boolean = false;

    beforeRefresh(handler: AppSidePanelHandler): void {
        if (!this.userHasReordered) {
            this.applyTabOrder(handler, this.tabOrder ?? this.leftSidebarTabOrder);
        }

        if (!this.tabOrderLoadStarted && handler.tabBar.titles.length > 0) {
            this.startTabOrderLoad(handler);
        }

        // Select a default tab only before any tab has been activated. When the user clicks
        // the active tab to collapse the panel, currentTitle becomes null but lastActiveTabIndex
        // remains set — re-selecting here would undo the collapse.
        if (
            handler.tabBar.titles.length > 0 &&
            !handler.tabBar.currentTitle &&
            handler.getLastActiveTabIndex() === undefined
        ) {
            handler.tabBar.currentTitle = handler.tabBar.titles[0];
        }
    }

    private get tabOrderKey(): string {
        return 'lvgl-tab-order-left';
    }

    private getTabId(title: { owner?: { id?: string } }): string {
        return title.owner?.id ?? '';
    }

    private rankIn(order: readonly string[], id: string): number {
        const i: number = order.indexOf(id);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    }

    private applyTabOrder(handler: AppSidePanelHandler, order: readonly string[]): void {
        if (handler.tabBar.titles.length <= 1) return;

        const current: Title<LuminoWidget>[] = [...handler.tabBar.titles];
        const sorted: Title<LuminoWidget>[] = [...current].sort((a, b) => {
            const rA: number = this.rankIn(order, this.getTabId(a));
            const rB: number = this.rankIn(order, this.getTabId(b));
            if (rA !== rB) return rA - rB;
            return a.label.localeCompare(b.label);
        });

        this.applyingOrder = true;
        try {
            sorted.forEach((title, index) => {
                if (handler.tabBar.titles[index] !== title) {
                    handler.tabBar.insertTab(index, title);
                }
            });
        } finally {
            this.applyingOrder = false;
        }
    }

    private saveTabOrder(handler: AppSidePanelHandler): void {
        const order: string[] = [...handler.tabBar.titles].map((title) => this.getTabId(title));
        this.storageService.setData(this.tabOrderKey, order);
    }

    private mergeSavedTabOrder(saved: readonly string[] | undefined): readonly string[] {
        if (!saved) return this.leftSidebarTabOrder;

        const merged: string[] = [...saved];
        for (const defaultId of this.leftSidebarTabOrder) {
            if (merged.includes(defaultId)) continue;

            const defaultIndex: number = this.leftSidebarTabOrder.indexOf(defaultId);
            let previousDefaultId: string | undefined;
            for (let index: number = defaultIndex - 1; index >= 0; index--) {
                const candidateId: string = this.leftSidebarTabOrder[index];
                if (!merged.includes(candidateId)) continue;

                previousDefaultId = candidateId;
                break;
            }
            const insertAfterIndex: number = previousDefaultId ? merged.indexOf(previousDefaultId) : -1;
            merged.splice(insertAfterIndex + 1, 0, defaultId);
        }

        return merged;
    }

    private startTabOrderLoad(handler: AppSidePanelHandler): void {
        this.tabOrderLoadStarted = true;
        (async () => {
            const saved: string[] | undefined = await this.storageService.getData<string[]>(this.tabOrderKey);
            this.tabOrder = this.mergeSavedTabOrder(saved);
            this.applyTabOrder(handler, this.tabOrder);

            handler.tabBar.tabMoved.connect(() => {
                if (this.applyingOrder) return;
                this.userHasReordered = true;
                this.saveTabOrder(handler);
            });
        })();
    }
}
